import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsRh, useJoursFeries, useJoursOuvrables } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

/** Calendrier RH (lot RH-1) : jours fériés administrés et vérification du calcul des jours ouvrables (lundi à vendredi, fériés exclus). */
export function CalendrierRh() {
  const moi = useSession()!.utilisateur;
  const peutModifier = moi.profils.some((p) => p === 'ADMINISTRATEUR' || p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES');
  const annee = new Date().getFullYear();
  const [exercice, setExercice] = useState(annee);
  const { data, isLoading, error } = useJoursFeries(exercice);
  const actions = useActionsRh();
  const [nouveau, setNouveau] = useState({ date: '', libelle: '' });
  const [calcul, setCalcul] = useState({ debut: '', reprise: '' });
  const ouvrables = useJoursOuvrables(calcul.debut, calcul.reprise);
  const ajouter = async () => {
    try {
      await actions.ajouterJourFerie.mutateAsync(nouveau);
      toast.success('Jour férié ajouté.');
      setNouveau({ date: '', libelle: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ajout impossible.');
    }
  };
  const supprimer = async (id: string) => {
    try {
      await actions.supprimerJourFerie.mutateAsync(id);
      toast.success('Jour férié retiré.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Suppression impossible.');
    }
  };
  return (
    <div>
      <PageHeader titre="Calendrier RH" sousTitre="Jours fériés exclus des jours ouvrables des congés et des délais ; liste indicative à vérifier par les Ressources humaines." />
      <OngletsAdministration />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Jours fériés
              <SelectNatif value={String(exercice)} onChange={(e) => setExercice(Number(e.target.value))}>
                {[annee - 1, annee, annee + 1, annee + 2].map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </SelectNatif>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {peutModifier && (
              <div className="flex flex-wrap items-end gap-3" data-testid="ajout-ferie">
                <div className="space-y-1.5">
                  <Label htmlFor="fdate">Date</Label>
                  <Input id="fdate" type="date" value={nouveau.date} onChange={(e) => setNouveau({ ...nouveau, date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="flib">Libellé</Label>
                  <Input id="flib" className="w-72" value={nouveau.libelle} onChange={(e) => setNouveau({ ...nouveau, libelle: e.target.value })} placeholder="Journée des Martyrs" />
                </div>
                <Button onClick={ajouter} disabled={!nouveau.date || nouveau.libelle.trim().length < 2 || actions.ajouterJourFerie.isPending}>
                  <Plus className="size-4" /> Ajouter
                </Button>
              </div>
            )}
            {isLoading ? (
              <Chargement />
            ) : error ? (
              <ErreurBloc erreur={error} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Libellé</TableHead>
                    {peutModifier && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data ?? []).map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{dateFr(f.date)}</TableCell>
                      <TableCell>{f.libelle}</TableCell>
                      {peutModifier && (
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" aria-label={`Retirer le ${f.date}`} onClick={() => supprimer(f.id)}>
                            <Trash2 className="size-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!data?.length && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground">
                        Aucun jour férié pour {exercice}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vérifier un décompte</CardTitle>
            <CardDescription>Début = premier jour non travaillé ; reprise = premier jour travaillé après l'absence (décision du 2 novembre 2026, point 10).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cdebut">Début de l'absence</Label>
              <Input id="cdebut" type="date" value={calcul.debut} onChange={(e) => setCalcul({ ...calcul, debut: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="creprise">Reprise de service</Label>
              <Input id="creprise" type="date" value={calcul.reprise} onChange={(e) => setCalcul({ ...calcul, reprise: e.target.value })} />
            </div>
            {ouvrables.data && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="resultat-ouvrables">
                <div className="text-2xl font-semibold">{ouvrables.data.joursOuvrables} jour{ouvrables.data.joursOuvrables > 1 ? 's' : ''} ouvrable{ouvrables.data.joursOuvrables > 1 ? 's' : ''}</div>
                <div className="text-muted-foreground">Dernier jour d'absence : {ouvrables.data.dernierJour ? dateFr(ouvrables.data.dernierJour) : '—'}</div>
                {ouvrables.data.feries.length > 0 && <div className="text-muted-foreground">Fériés exclus : {ouvrables.data.feries.map((f) => `${dateFr(f.date)} (${f.libelle})`).join(', ')}</div>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
